/*
  Houseplant v2 — Collection results (sections/hp-main-collection.liquid)

  Filter and sort without a page load, via Shopify's Section Rendering API.

  Deliberately NOT assets/facets.js. That file does the same job but is welded
  to the base theme's DOM — it looks up #ProductGridContainer, #ProductCount,
  #ProductCountMobile, #ProductCountDesktop, #FacetDrawer, #FacetSortFiltersForm,
  #FacetFiltersForm, #FacetFiltersFormMobile, .js-filter, .collection,
  layout-switcher and .list-view__item--active by name, so reusing it would mean
  reproducing a drawer, three product counts and a layout switcher this design
  does not have. What is kept is the technique, not the code.

  The whole thing degrades cleanly: with JavaScript off the form is an ordinary
  GET form with a submit button, and every URL this produces is one the server
  can render on its own.
*/

class HpCollectionResults extends HTMLElement {
  /* Lead distance before the row, and the minimum time the spinner stays up.
     Together they are the whole feel of the load: the lead decides WHEN it
     starts, the floor decides whether anyone sees it. */
  static LOAD_LEAD_PX = 150;
  static MIN_SPINNER_MS = 450;

  /* How long a re-render holds the outgoing results on their way out. Matches
     --hp-duration-hover, the length of the opacity transition in the section's
     CSS, so the old list has actually finished fading before the new one
     replaces it. Without it a CACHED response swaps on the same frame as the
     click and the grid simply jumps, which is the opposite of what prefetching
     the quick links is for. */
  static FADE_MS = 200;

  /* The address-bar name for a quick link: ?filter=<the tag minus ql->. */
  static QUICK_PARAM = 'filter';

  connectedCallback() {
    this.sectionId = this.dataset.sectionId;
    this.baseUrl = this.dataset.baseUrl || window.location.pathname;
    /* Cache keyed by URL. Ticking a box and unticking it is the single most
       common interaction here, and it should not cost two round trips. */
    this.cache = new Map();

    this.onChange = this.onChange.bind(this);
    this.onSubmit = this.onSubmit.bind(this);
    this.onPopState = this.onPopState.bind(this);
    this.onDocumentClick = this.onDocumentClick.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);

    this.onClick = this.onClick.bind(this);
    this.onIntent = this.onIntent.bind(this);
    this.renderId = 0;
    /* The quick link in force, as its tag handle ('' = the whole collection).
       Internal only: it decides which URL is FETCHED, never what the address
       bar shows. See `render`. */
    this.scope = '';

    this.addEventListener('change', this.onChange);
    this.addEventListener('submit', this.onSubmit);
    this.addEventListener('click', this.onClick);
    this.addEventListener('pointerover', this.onIntent);
    this.addEventListener('focusin', this.onIntent);
    this.addEventListener('touchstart', this.onIntent, { passive: true });
    this.prefetchQuickLinks();
    window.addEventListener('popstate', this.onPopState);
    document.addEventListener('click', this.onDocumentClick);
    this.addEventListener('keydown', this.onKeyDown);

    this.loadingMore = false;
    this.onScrollCheck = this.onScrollCheck.bind(this);
    this.lastScrollCheck = 0;
    window.addEventListener('scroll', this.onScrollCheck, { passive: true });
    window.addEventListener('resize', this.onScrollCheck, { passive: true });
    this.initInfinite();
    this.bootQuickLink();

    /* One panel at a time. Two <details> can perfectly well be open at once,
       and at 390 the Filter and Sort panels then overlap each other — the sort
       list drawn straight over the filter groups.

       Bound to the summary's CLICK, not to the details' `toggle`. `toggle` is
       queued rather than dispatched synchronously, so with two opens in quick
       succession the handlers run after BOTH elements are already open and the
       first one to fire closes the second — the panel the visitor opened last
       is the one that disappears. Click runs before the element toggles, so the
       ordering is never in doubt. Keyboard activation of a summary dispatches a
       click too, so this covers Enter and Space as well as the pointer. */
    this.querySelectorAll('[data-hp-dropdown] > summary').forEach((summary) => {
      summary.addEventListener('click', () => {
        const mine = summary.parentElement;
        /* Already open means this click is closing it — nothing to arbitrate. */
        if (mine.open) return;
        this.querySelectorAll('[data-hp-dropdown][open]').forEach((other) => {
          if (other !== mine) other.open = false;
        });
      });
    });
  }

  disconnectedCallback() {
    this.infiniteObserver?.disconnect();
    window.removeEventListener('scroll', this.onScrollCheck);
    window.removeEventListener('resize', this.onScrollCheck);
    window.removeEventListener('popstate', this.onPopState);
    document.removeEventListener('click', this.onDocumentClick);
  }

  get form() {
    return this.querySelector('[data-hp-collection-form]');
  }

  /* An empty field must not reach the URL. A price filter the visitor never
     touched would otherwise submit as `filter.v.price.gte=` — which Shopify
     reads as a real, zero-valued filter, and the collection silently empties. */
  params() {
    const params = new URLSearchParams();
    const defaultSort = this.dataset.defaultSort;
    new FormData(this.form).forEach((value, key) => {
      if (String(value).trim() === '') return;
      /* The sort radio is always checked (the section pre-checks the default),
         so without this every URL carried `?sort_by=manual` — harmless, but it
         is the address a quick link puts in a shared link, and it would also
         split the prefetch cache from the clean URL for no reason. */
      if (key === 'sort_by' && value === defaultSort) return;
      params.append(key, value);
    });
    return params.toString();
  }

  /* ── THE ELEMENT WRAPS THE CONTROLS *AND* THE RESULTS, SO IT HEARS THE CARDS ──
     `change` bubbles, and this listener is on the wrapper rather than on the
     form, so every control inside a product card reaches it too — including
     hp-product-card's colour-swatch radios. Unguarded, choosing a swatch ran a
     full filter re-render.

     The symptom did not look like a re-render, which is why it reads as an image
     bug: `params()` serialises the FORM only, so the swatch changes nothing in
     the URL and the fetch returns the SAME markup. The grid flashes, the radio
     snaps back to the server's default, and hp-card-swatch.js's image swap is
     wiped by the innerHTML swap a moment after it happened. Reported as "the
     swatch doesn't change the image, everything just flashes white".

     It did not affect the homepage showcase because that section has no
     `hp-collection-results` wrapper and no change listener — the same cards work
     fine there, which is the tell that the fault is in the container, not the
     card.

     Guarding on the FORM rather than excluding swatches by name, for the same
     reason cart-items guards on `[name="updates[]"]`: it protects any control
     added to a card later without anyone having to remember this listener
     exists. A missing form (a collection with no filters or sort) can therefore
     never re-render either, which is correct.

     Collection page only: hp-main-search.liquid loads this file but deliberately
     does not use the element (its own note says this script is hard-wired to
     `hp-collection-results` and a collection URL), and its grid sits outside
     both of its forms. Nothing to fix there. */
  onChange(event) {
    const form = this.form;
    if (!form || !form.contains(event.target)) return;
    this.render(this.params());
  }

  /* ── QUICK LINKS ─────────────────────────────────────────────────────────────
     A chip is a toggle button carrying its tag handle in
     `data-hp-quick-link-scope`. Pressing it sets the SCOPE — the path segment
     the next fetch goes to — and pressing the pressed one clears it. The live
     form params ride along unchanged, so choosing Ashtrays while sorted by
     price stays sorted by price. The chips flip on the click, before the
     fetch, so the row answers at once even on a slow response; the server's
     own pressed state is copied back by `syncQuickLinks` after the swap.

     Clear all, the empty state's way out and the fallback pager are links to
     the CLEAN collection URL (no tag), intercepted here so they keep the scope
     without ever putting it in an href. */
  onClick(event) {
    const chip = event.target.closest('[data-hp-quick-link]');
    if (chip && this.contains(chip)) {
      event.preventDefault();
      const next = chip.getAttribute('aria-pressed') === 'true' ? '' : chip.dataset.hpQuickLinkScope;
      this.pressChip(next);
      this.closeDropdowns();
      this.render(this.params(), true, next);
      return;
    }

    const link = event.target.closest(
      'a[data-hp-collection-clear], a[data-hp-collection-reset], [data-hp-pager] a[href]'
    );
    if (!link || !this.contains(link)) return;
    if (event.defaultPrevented || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();

    /* The empty state's "Clear all" resets EVERYTHING, quick link included:
       with nothing on screen, filters and quick link are one question. */
    if (link.matches('[data-hp-collection-reset]')) {
      this.pressChip('');
      this.render('', true, '');
      return;
    }

    const params = link.matches('[data-hp-collection-clear]') ? '' : this.readQuery(new URL(link.href).search).params;
    this.render(params, true, this.scope);
  }

  pressChip(scope) {
    this.querySelectorAll('[data-hp-quick-link]').forEach((chip) => {
      const on = scope !== '' && chip.dataset.hpQuickLinkScope === scope;
      chip.classList.toggle('is-active', on);
      chip.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
  }

  /* ── ?filter=<name>: DIRECT LINKS, RELOAD, BACK FROM A PRODUCT ──────────────
     The address bar carries the quick link by its PUBLIC name — the tag minus
     `ql-` (`data-hp-quick-link-slug`) — so staff can link straight to a view
     and any chip's view can be copied. The tag itself still only ever appears
     in a FETCHED url (`sectionUrl`).

     Liquid cannot read query parameters, so the server rendered the whole
     collection and the choice is applied here, once, in place. The inline
     script in the section has already faded the results before first paint;
     `hpQlBoot` tells its fallback timer that this script took over.

     An unknown or locked name shows the whole collection and is REMOVED from
     the address bar, so the URL never claims a view that is not on screen. A
     name is matched case-insensitively, and the full tag is accepted too, but
     only the public name is ever written back. */
  bootQuickLink() {
    this.dataset.hpQlBoot = '1';
    const { slug, params } = this.readQuery(window.location.search);
    if (!slug) return;

    const scope = this.scopeForSlug(slug);
    if (!scope) {
      this.setLoading(false);
      window.history.replaceState(window.history.state, '', this.visibleUrl('', params));
      this._renderedKey = `|${params}`;
      return;
    }
    this.pressChip(scope);
    this.render(params, false, scope);
  }

  /* Splits a query string into the quick-link name and everything else (the
     form's own filter / sort / page params). */
  readQuery(search) {
    const query = new URLSearchParams(search);
    const slug = (query.get(this.constructor.QUICK_PARAM) || '').trim().toLowerCase();
    query.delete(this.constructor.QUICK_PARAM);
    return { slug, params: query.toString() };
  }

  scopeForSlug(slug) {
    if (!slug) return '';
    const chip = [...this.querySelectorAll('[data-hp-quick-link]')].find(
      (c) => c.dataset.hpQuickLinkSlug === slug || c.dataset.hpQuickLinkScope === slug
    );
    return chip ? chip.dataset.hpQuickLinkScope : null;
  }

  slugForScope(scope) {
    if (!scope) return '';
    const chip = [...this.querySelectorAll('[data-hp-quick-link]')].find(
      (c) => c.dataset.hpQuickLinkScope === scope
    );
    return chip?.dataset.hpQuickLinkSlug || '';
  }

  /* What the address bar shows: the collection, ?filter=<public name> first,
     then the form's params. NEVER the tag. */
  visibleUrl(scope, params) {
    const query = new URLSearchParams();
    const slug = this.slugForScope(scope);
    if (slug) query.set(this.constructor.QUICK_PARAM, slug);
    new URLSearchParams(params).forEach((value, key) => query.append(key, value));
    const search = query.toString();
    return `${this.baseUrl}${search ? `?${search}` : ''}`;
  }

  /* ── WHY THIS FEELS INSTANT: THE RESPONSE IS ALREADY HERE ───────────────────
     A quick link costs one Section Rendering round trip, and "real time" means
     not waiting for it. Every chip's response is requested once the page is
     idle, and again on hover, focus or touch in case the live params have
     moved on since (a sort chosen after load is a different URL). The cache
     holds the PROMISE, not the text, so a click landing while that request is
     still in flight joins it rather than sending a second. */
  onIntent(event) {
    const chip = event.target.closest?.('[data-hp-quick-link]');
    if (!chip) return;
    const target = chip.getAttribute('aria-pressed') === 'true' ? '' : chip.dataset.hpQuickLinkScope;
    this.fetchSection(this.sectionUrl(target, this.params())).catch(() => {});
  }

  prefetchQuickLinks() {
    if (!this.querySelector('[data-hp-quick-link]')) return;
    /* Respect a visitor who asked to save data: intent prefetch still runs,
       because that is a request they are about to make anyway. */
    if (navigator.connection?.saveData) return;
    const run = () => {
      this.querySelectorAll('[data-hp-quick-link]').forEach((chip) => {
        this.fetchSection(this.sectionUrl(chip.dataset.hpQuickLinkScope, this.params())).catch(() => {});
      });
    };
    /* A timer as well as idle: requestIdleCallback is a hint the browser may
       never act on, and the guarantee must not depend on it (CLAUDE.md, the
       image-fade deadline). */
    if ('requestIdleCallback' in window) window.requestIdleCallback(run, { timeout: 2500 });
    else setTimeout(run, 1500);
  }

  /* The ONLY place a tag reaches a URL, and that URL is only ever fetched. */
  sectionUrl(scope, params) {
    const path = scope ? `${this.baseUrl}/${encodeURIComponent(scope)}` : this.baseUrl;
    return `${path}?section_id=${this.sectionId}${params ? `&${params}` : ''}`;
  }

  fetchSection(url) {
    let pending = this.cache.get(url);
    if (!pending) {
      pending = fetch(url).then((response) => {
        if (!response.ok) throw new Error(`Section render failed: ${response.status}`);
        return response.text();
      });
      /* A failed request must not stay cached, or every later click on that
         chip replays the failure without trying again. */
      pending.catch(() => this.cache.delete(url));
      this.cache.set(url, pending);
    }
    return pending;
  }

  onSubmit(event) {
    /* The submit button exists for the no-JS path; once we are here, handle it
       in place rather than letting the browser navigate. */
    event.preventDefault();
    this.render(this.params());
    this.closeDropdowns();
  }

  /* Everything from the LOCATION: ?filter= names the quick link, the rest is
     the form's. A hash-only change (a skip link) fires popstate too and would
     re-render an unchanged list, so that is skipped. */
  onPopState() {
    const { slug, params } = this.readQuery(window.location.search);
    const scope = this.scopeForSlug(slug) || '';
    if (`${scope}|${params}` === this.renderedKey) return;
    this.pressChip(scope);
    this.render(params, false, scope);
  }

  /* `<details>` has no light-dismiss of its own, so outside-click and Escape
     are added here. Everything else about opening and closing is the element's
     own behaviour. */
  onDocumentClick(event) {
    if (this.contains(event.target)) return;
    this.closeDropdowns();
  }

  onKeyDown(event) {
    if (event.key !== 'Escape') return;
    const open = this.querySelector('[data-hp-dropdown][open]');
    if (!open) return;
    this.closeDropdowns();
    open.querySelector('summary')?.focus();
  }

  closeDropdowns() {
    this.querySelectorAll('[data-hp-dropdown][open]').forEach((d) => {
      d.open = false;
    });
  }

  get renderedKey() {
    if (this._renderedKey === undefined) {
      this._renderedKey = `|${this.readQuery(window.location.search).params}`;
    }
    return this._renderedKey;
  }

  async render(params, pushState = true, scope = this.scope) {
    const url = this.sectionUrl(scope, params);
    const visible = this.visibleUrl(scope, params);
    /* ── ONLY THE LATEST REQUEST MAY SWAP ──────────────────────────────────────
       Two quick clicks are two fetches, and the slower one can land second —
       an older list arriving last and overwriting the one the visitor chose.
       Every render takes a number; a response that is no longer the newest is
       dropped on arrival. */
    const id = ++this.renderId;
    this.setLoading(true);
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const hold = reduced ? 0 : this.constructor.FADE_MS;
    const startedAt = Date.now();

    try {
      const html = await this.fetchSection(url);
      const remaining = hold - (Date.now() - startedAt);
      if (remaining > 0) await new Promise((resolve) => setTimeout(resolve, remaining));
      if (id !== this.renderId) return;

      /* Scope first: swap() rewrites the fallback pager's hrefs from it. */
      this.scope = scope;
      this.swap(html);
      this._renderedKey = `${scope}|${params}`;
      const state = { hpCollection: params, hpQuickLink: scope };
      /* Not pushing (Back / Forward, or a direct link) still REPLACES, which
         normalises a hand-typed ?filter=ASHTRAYS to the canonical name. */
      if (pushState) window.history.pushState(state, '', visible);
      else window.history.replaceState(state, '', visible);
      /* Under a quick link, the way back out is the next likely click. */
      if (scope) this.fetchSection(this.sectionUrl('', params)).catch(() => {});
    } catch (error) {
      if (id !== this.renderId) return;
      /* Anything unexpected falls back to a real navigation, so a network blip
         costs a page load rather than leaving a dead control row. WITHOUT the
         quick link: the reloaded page would re-apply ?filter= through this
         same path, and a fetch that keeps failing would reload forever. */
      console.error(error);
      window.location.href = this.visibleUrl('', params);
    } finally {
      if (id === this.renderId) this.setLoading(false);
    }
  }

  setLoading(state) {
    this.classList.toggle('is-loading', state);
    this.setAttribute('aria-busy', state ? 'true' : 'false');
  }

  /* ── Infinite scroll ───────────────────────────────────────────────────────
   * The numbered pager stays in the markup as the fallback; `is-infinite` is
   * what hides it, and it is added HERE — after the observer exists — so a
   * browser without IntersectionObserver, or a page where this script never
   * ran, keeps a working pager instead of a list that just stops.
   */
  initInfinite() {
    this.infiniteObserver?.disconnect();

    const sentinel = this.querySelector('[data-hp-infinite]');
    if (!sentinel || !('IntersectionObserver' in window)) {
      this.classList.remove('is-infinite');
      return;
    }

    this.classList.add('is-infinite');

    /* ── THE LEAD IS SHORT ON PURPOSE ────────────────────────────────────────
       This was 400px, which pre-loaded the next page so early that the row was
       still below the fold when the fetch ran and finished — so the spinner was
       never actually SEEN and the list appeared to extend by magic. Reported as
       "almost too fast".

       150 starts the load as the row comes into view, which is what makes the
       spinner do its job. Paired with the minimum display below; neither works
       without the other. */
    this.infiniteObserver = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) this.loadMore();
      },
      { rootMargin: `${this.constructor.LOAD_LEAD_PX}px 0px` }
    );
    this.infiniteObserver.observe(sentinel);
  }

  /* ── THE SCROLL FALLBACK IS NOT BELT-AND-BRACES, IT IS THE GUARANTEE ───────
   * `is-infinite` hides the numbered pager, and it is added when the OBSERVER IS
   * CREATED — not when it first fires. So an environment where
   * IntersectionObserver exists but never delivers a callback gets no pager AND
   * no loading: a collection that simply ends. That is not hypothetical; it is
   * what the browser pane does, and CLAUDE.md already records an
   * IntersectionObserver silently never firing there during the image-fade work.
   * The lesson recorded then applies exactly: a guarantee conditional on a
   * mechanism that can silently not run is not a guarantee.
   *
   * So the same viewport test also runs on scroll. The observer is still the
   * primary trigger — it is cheaper and fires without user input — and this
   * costs one throttled rect read per 150ms of scrolling.
   *
   * Throttled on a TIMESTAMP rather than requestAnimationFrame, because rAF is
   * frozen in the pane too (CLAUDE.md), which would make the fallback share the
   * primary's failure mode instead of covering it.
   */
  onScrollCheck() {
    const now = Date.now();
    if (now - this.lastScrollCheck < 150) return;
    this.lastScrollCheck = now;

    if (this.loadingMore) return;
    const sentinel = this.querySelector('[data-hp-infinite]');
    if (!sentinel || !sentinel.dataset.nextUrl) return;

    const rect = sentinel.getBoundingClientRect();
    if (rect.top <= window.innerHeight + this.constructor.LOAD_LEAD_PX) this.loadMore();
  }

  async loadMore() {
    if (this.loadingMore) return;

    const sentinel = this.querySelector('[data-hp-infinite]');
    const nextUrl = sentinel?.dataset.nextUrl;
    if (!sentinel || !nextUrl) return;

    this.loadingMore = true;
    const startedAt = Date.now();
    sentinel.classList.add('is-loading');

    try {
      /* Built from the sentinel's own URL so the live filter and sort params
         ride along; only section_id is added. */
      const url = new URL(nextUrl, window.location.origin);
      url.searchParams.set('section_id', this.sectionId);

      const response = await fetch(url);
      if (!response.ok) throw new Error(`Load more failed: ${response.status}`);

      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const grid = this.querySelector('.hp-collection__grid');
      const incoming = doc.querySelectorAll('.hp-collection__grid > *');
      if (!grid || incoming.length === 0) throw new Error('Load more returned no items');

      /* ── A PERCEPTIBILITY FLOOR, NOT A THROTTLE ───────────────────────────
         The fetch is usually faster than the spinner takes to register as
         anything, so without this the row flickers and the content simply
         appears — which reads as a glitch rather than as loading. Holding the
         APPEND (not the fetch) until the spinner has been up for
         MIN_SPINNER_MS costs nothing when the network is slow, because by then
         the floor has already passed. */
      const elapsed = Date.now() - startedAt;
      const remaining = this.constructor.MIN_SPINNER_MS - elapsed;
      if (remaining > 0) await new Promise((resolve) => setTimeout(resolve, remaining));

      incoming.forEach((item) => grid.appendChild(document.importNode(item, true)));
      this.placePromos();

      /* The fetched page carries its OWN sentinel. Its presence is what says
         whether there is another page — inferring it from a page counter would
         drift the moment a filter changes the total. */
      const nextSentinel = doc.querySelector('[data-hp-infinite]');
      if (nextSentinel && nextSentinel.dataset.nextUrl) {
        sentinel.dataset.nextUrl = nextSentinel.dataset.nextUrl;
      } else {
        this.infiniteObserver?.disconnect();
        sentinel.remove();
        /* That was the last page: a tile whose card number the list never
           reached goes at the end rather than staying unplaced for good. */
        this.flushPromos();
      }

      /* The announcement string is rendered by Liquid and travels with the page
         that was just appended, so pluralization and translation stay in the
         locale files. It replaced a hardcoded English template literal, and the
         reason it is not simply a locale string interpolated here is in the
         section's comment: Shopify picks the plural form from the number at
         render time, and this theme ships Czech, which has three of them. */
      const progress = doc.querySelector('[data-hp-infinite-progress]');
      if (progress) window.hpAnnounce(progress.textContent);
    } catch (error) {
      /* Give the pager back rather than leaving the visitor at a dead end. */
      console.error(error);
      this.infiniteObserver?.disconnect();
      this.classList.remove('is-infinite');
      sentinel.remove();
    } finally {
      this.loadingMore = false;
      sentinel.classList.remove('is-loading');

      /* RE-OBSERVE, and this is load-bearing. IntersectionObserver fires on a
         CHANGE of intersection, so if the appended page is short enough that the
         sentinel is still on screen, no further callback ever arrives and
         loading stops halfway down a collection. Re-observing re-reports the
         current state, which continues the chain until the sentinel is pushed
         out of view or removed. */
      if (sentinel.isConnected && this.infiniteObserver) {
        this.infiniteObserver.unobserve(sentinel);
        this.infiniteObserver.observe(sentinel);
      }
    }
  }

  /* ── Promo tiles past page one ─────────────────────────────────────────────
   * The section places tiles by CARD count, and only page one can count cards
   * from the top (with Split colours on, a page's card count is unknown to the
   * next). Tiles further down wait in [data-hp-promo-pending] as <template>s;
   * once the list holds a tile's card, it goes in straight after it (after any
   * tile already there, so two at one position keep their block order). */
  placePromos() {
    const grid = this.querySelector('.hp-collection__grid');
    const pending = this.querySelectorAll('[data-hp-promo-pending] > template[data-hp-promo-after]');
    if (!grid || pending.length === 0) return;

    const cards = grid.querySelectorAll(':scope > .hp-collection__item:not(.hp-collection-promo)');
    pending.forEach((template) => {
      const after = Number(template.dataset.hpPromoAfter);
      if (!(after >= 1) || cards.length < after) return;
      let anchor = cards[after - 1];
      while (anchor.nextElementSibling?.classList.contains('hp-collection-promo')) {
        anchor = anchor.nextElementSibling;
      }
      anchor.after(template.content.cloneNode(true));
      template.remove();
    });
  }

  /* The end of the list, reached with tiles still pending (a filtered or
     quick-linked list shorter than their "after card"). The server does the
     same for a list that fits on one page; this is the infinite-scroll half. */
  flushPromos() {
    const grid = this.querySelector('.hp-collection__grid');
    if (!grid) return;
    this.querySelectorAll('[data-hp-promo-pending] > template[data-hp-promo-after]').forEach((template) => {
      grid.appendChild(template.content.cloneNode(true));
      template.remove();
    });
  }

  swap(html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const incoming = doc.querySelector('hp-collection-results');
    if (!incoming) return;

    this.syncQuickLinks(incoming);

    /* Results are replaced wholesale. */
    const results = this.querySelector('[data-hp-collection-results]');
    const nextResults = incoming.querySelector('[data-hp-collection-results]');
    if (results && nextResults) results.innerHTML = nextResults.innerHTML;

    /* A filter or sort used to replace the grid in complete silence, while
       infinite scroll on this same page announced every append — our own two
       paths disagreeing about whether a changed product list is worth saying.
       The text is rendered by Liquid and swapped in with the grid, so the
       count cannot drift from what it counts and no plural rule lives here.
       Read AFTER the swap; before it, this is the outgoing count. */
    const count = this.querySelector('[data-hp-collection-count]');
    if (count) window.hpAnnounce(count.textContent);

    /* Controls are replaced PANEL BY PANEL, keeping each <details> element
       itself. Replacing the whole form would discard the open attribute and the
       panel would slam shut on every tick — the counts have to update while the
       list the visitor is reading stays where it is. */
    this.querySelectorAll('[data-hp-dropdown]').forEach((current) => {
      const next = incoming.querySelector(`[data-hp-dropdown]#${CSS.escape(current.id)}`);
      if (!next) return;

      const wasOpen = current.open;
      const active = document.activeElement;
      const activeName = current.contains(active) ? active.name : null;
      const activeValue = current.contains(active) ? active.value : null;

      current.querySelector('.hp-dropdown__panel').innerHTML =
        next.querySelector('.hp-dropdown__panel').innerHTML;

      const value = current.querySelector('[data-hp-dropdown-value]');
      const nextValue = next.querySelector('[data-hp-dropdown-value]');
      if (value && nextValue) value.textContent = nextValue.textContent;

      current.open = wasOpen;

      /* Focus follows the control the visitor just used into its replacement,
         so keyboard position is not lost on every change. */
      if (activeName) {
        const restored = current.querySelector(
          `[name="${CSS.escape(activeName)}"]${activeValue ? `[value="${CSS.escape(activeValue)}"]` : ''}`
        );
        restored?.focus();
      }
    });

    /* The results were replaced wholesale, so the old sentinel is gone and the
       observer is pointing at a detached node. Re-arm against the new one. */
    this.initInfinite();

    /* A tag render's pager links point at the TAG URL. The pager only shows
       when infinite scroll is unavailable, but an href is an href: rewrite
       each to the public form — the collection path plus ?filter=<name> — so
       even a middle-click opens the same view without exposing the tag. */
    this.querySelectorAll('[data-hp-pager] a[href]').forEach((a) => {
      const u = new URL(a.href, window.location.origin);
      const { params } = this.readQuery(u.search);
      a.setAttribute('href', this.visibleUrl(this.scope, params));
    });
  }

  /* The server's chips are the truth about which one is pressed. Copied
     ATTRIBUTE BY ATTRIBUTE onto the live chips, never by innerHTML, so the
     chip the visitor just pressed keeps keyboard focus. Back and forward land
     here too, which keeps the row honest when the click handler never ran. */
  syncQuickLinks(incoming) {
    this.querySelectorAll('[data-hp-quick-link]').forEach((chip) => {
      const next = incoming.querySelector(
        `[data-hp-quick-link="${CSS.escape(chip.dataset.hpQuickLink)}"]`
      );
      if (!next) return;
      const on = next.getAttribute('aria-pressed') === 'true';
      chip.classList.toggle('is-active', on);
      chip.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
  }
}

if (!customElements.get('hp-collection-results')) {
  customElements.define('hp-collection-results', HpCollectionResults);
}
